import { useMemo, useRef } from 'react';
import type { TrackingPropsInterface } from '@marriott/mi-ui-library';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  ABody,
  ABodyColor,
  ABodySize,
  AEyebrow,
  AEyebrowVariant,
  AHeader,
  AHeaderSize,
  AIcon,
  AIconSize,
  MTextBlockSimpleAlignment,
} from '@marriott/mi-web-ui-kit';
import { useSession } from '@marriott/mi-headless-utils';
import { convertToCommaSeparatedNumber } from '@marriott/shared/mi-helper-utils';
import type { MerchandisingLayeredFeatureProps } from './MerchandisingLayeredFeature.types';
import { MarkDownText } from '../../molecules/markDown/MarkDown';
import { SkeletonLoaderStyles } from '../../skeleton-loader.styles';
import {
  LayeredFeatureTextBlockWrapper,
  StyledPhotoCaption,
  StyledPhotoCaptionText,
  StyledMerchandisingLayeredFeature,
} from './MerchandisingLayeredFeature.styles';
import { MathEquation, EqColumn, EqOperator } from './MerchandisingLayeredFeatureMath';
import clsx from 'clsx';
import {
  Button,
  getIconClassFromToken,
  getStyleClassPerTheme,
  Icon,
  Image,
  size,
  Types,
  useCheckBreakpoint,
} from '@marriott/mi-ui-library';
import { MLogoContainer, MLogoContainerBackgroundColor } from '@marriott/mi-web-ui-kit';
import { AuthoringUtils } from '@adobe/aem-spa-page-model-manager';
import { getTrackingPropertiesDynamic } from '../../utils/helpers';
import type { TrackingProps } from '../MerchandisingHeroBanner/MerchandisingHeroBanner.types';
import type { IMetaData } from '../../types/dcaTypes';

const { INTERNAL, EXTERNAL } = Types.LinkTargetType;
interface LayeredFeatureCtaTracking {
  trackingProperties: TrackingPropsInterface;
  linkType: string;
}

function useLayeredFeatureButtonTracking(props: MerchandisingLayeredFeatureProps): {
  primary: LayeredFeatureCtaTracking;
  secondary: LayeredFeatureCtaTracking;
  tertiary: LayeredFeatureCtaTracking;
} {
  const { datalayer } = useSession();
  const {
    trackingProperties,
    type = 'cf',
    metadata,
    mboxParameter,
    isTargetContentError,
    headerMedium,
    descriptionLong = '',
    callToActionText2,
    callToActionText1,
    openInNewTab2,
    openInNewTab1,
    tertiaryOpenInANewTab,
    tertiaryLinkText,
  } = props;
  const isAuth = datalayer?.['mr_prof_authentication_state'] === 'authenticated';
  const isDCA = type === 'dca';
  return useMemo(() => {
    const baseTp = (trackingProperties ?? {}) as TrackingProps;
    const metaForDynamic: IMetaData = isDCA && metadata ? metadata : ({} as IMetaData);

    const DCATrackingProps = getTrackingPropertiesDynamic(
      baseTp,
      metaForDynamic,
      props,
      isAuth,
      isTargetContentError ?? false,
      mboxParameter ?? ''
    );

    const isEvergreenDcaFallback = Boolean((baseTp as Record<string, unknown>)?.['isDCAEnabled']);
    const trackingPropertiesDynamic: TrackingPropsInterface =
      !isDCA && !isEvergreenDcaFallback
        ? (() => {
            const CFTrackingProps = {
              ...DCATrackingProps,
              isDCAEnabled: false,
            } as TrackingPropsInterface;
            delete (CFTrackingProps as Record<string, unknown>)['clickTrackValSeparator'];
            return CFTrackingProps;
          })()
        : DCATrackingProps;

    // Genuine authored CF LFCs suffix the description with the CTA text (eVar165);
    // the evergreen DCA fallback keeps the raw 'dca' description so its click list3 msg
    // stays consistent with the impression list3 (msg=dca), matching a live DCA card.
    const appendCtaTextToDescription = !isDCA && !isEvergreenDcaFallback;
    return {
      primary: {
        trackingProperties: {
          ...trackingPropertiesDynamic,
          trackingTag: (metadata?.merTag ?? undefined) || baseTp?.trackingTag1,
          trackingDescription: `${DCATrackingProps.trackingDescription}${
            appendCtaTextToDescription ? `-${callToActionText1}` : ''
          }`,
        },
        linkType: openInNewTab1 ? EXTERNAL : INTERNAL,
      },
      secondary: {
        trackingProperties: {
          ...trackingPropertiesDynamic,
          trackingTag: baseTp?.trackingTag2,
          trackingDescription: `${DCATrackingProps.trackingDescription}${
            appendCtaTextToDescription ? `-${callToActionText2}` : ''
          }`,
        },
        linkType: openInNewTab2 ? EXTERNAL : INTERNAL,
      },
      tertiary: {
        trackingProperties: {
          ...trackingPropertiesDynamic,
          trackingTag: (metadata?.merTag ?? undefined) || baseTp?.trackingTag1,
          trackingDescription: `${DCATrackingProps.trackingDescription}${
            appendCtaTextToDescription ? `-${tertiaryLinkText}` : ''
          }`,
        },
        linkType: tertiaryOpenInANewTab ? EXTERNAL : INTERNAL,
      },
    };
  }, [
    callToActionText2,
    descriptionLong,
    headerMedium,
    isAuth,
    isTargetContentError,
    metadata,
    mboxParameter,
    openInNewTab1,
    openInNewTab2,
    props,
    tertiaryOpenInANewTab,
    trackingProperties,
    type,
  ]);
}

function useMathVariation(props: MerchandisingLayeredFeatureProps) {
  const { mathType = '', mathLabel1 = '', mathLabel2 = '', mathLabel3 = '', mathValue = '' } = props;
  const { datalayer } = useSession();

  const currencyType = (datalayer?.['prop_currency_type'] as string) ?? '';
  const currencyTotalStayAmount = parseFloat(`${datalayer?.['res_revenue_total_locale'] ?? '0'}`);
  const pointsTotalStayAmount = parseInt(`${datalayer?.['mr_prof_points_balance'] ?? '0'}`, 10);
  const currentNights = parseInt(`${datalayer?.['mr_prof_nights_booked_this_year'] ?? '0'}`, 10);

  const mt = mathType?.toLowerCase() ?? '';
  const isPointsVariation = mt === 'points';
  const isCurrencyVariation = mt === 'currency' && !!currencyType;
  const isNightsVariation = mt === 'nights';
  const isVariationsAvailable = isPointsVariation || isCurrencyVariation || isNightsVariation;

  const { value1, value2, value3 } = useMemo(() => {
    if (isPointsVariation) {
      const bonus = mathValue ? parseInt(mathValue.replace(/,/g, ''), 10) : 0;
      return {
        value1: convertToCommaSeparatedNumber(pointsTotalStayAmount),
        value2: mathValue,
        value3: convertToCommaSeparatedNumber(pointsTotalStayAmount + bonus),
      };
    }
    if (isCurrencyVariation) {
      const credit = mathValue ? parseFloat(mathValue.replace(/,/g, '')) : 0;
      return {
        value1: convertToCommaSeparatedNumber(currencyTotalStayAmount) + ' ' + currencyType,
        value2: mathValue + ' ' + currencyType,
        value3: convertToCommaSeparatedNumber(currencyTotalStayAmount + credit) + ' ' + currencyType,
      };
    }
    if (isNightsVariation) {
      const bonusNights = mathValue ? parseInt(mathValue.replace(/,/g, ''), 10) : 0;
      return {
        value1: convertToCommaSeparatedNumber(currentNights),
        value2: mathValue,
        value3: convertToCommaSeparatedNumber(currentNights + bonusNights),
      };
    }
    return { value1: '', value2: '', value3: '' };
  }, [
    isPointsVariation,
    isCurrencyVariation,
    isNightsVariation,
    mathValue,
    pointsTotalStayAmount,
    currencyTotalStayAmount,
    currencyType,
    currentNights,
  ]);

  return {
    variationDetails: { isVariationsAvailable, isPointsVariation, isCurrencyVariation, isNightsVariation },
    mathLabel1,
    mathLabel2,
    mathLabel3,
    value1,
    value2,
    value3,
  };
}

/** Header + description stack for the layered feature content column (co-located for readability). */
function LayeredFeatureTextContent({
  alignment = MTextBlockSimpleAlignment.Left,
  eyebrowText,
  eyebrowVariant = AEyebrowVariant.Secondary,
  headerMarkdown,
  headerSize = AHeaderSize.TitleM,
  description,
  bodySize,
  bodyColor = ABodyColor.Default,
}: {
  alignment?: MTextBlockSimpleAlignment;
  eyebrowText?: string;
  eyebrowVariant?: AEyebrowVariant;
  headerMarkdown: string;
  headerSize?: AHeaderSize;
  description?: string;
  bodySize?: ABodySize;
  bodyColor?: ABodyColor;
}) {
  const hasHeader = Boolean(headerMarkdown?.trim());

  return (
    <LayeredFeatureTextBlockWrapper
      className="d-flex flex-column"
      $centered={alignment === MTextBlockSimpleAlignment.Center}
    >
      {eyebrowText ? <AEyebrow variant={eyebrowVariant}>{eyebrowText}</AEyebrow> : null}
      {hasHeader ? (
        <MarkDownText
          className="t-title-s"
          fallback={<AHeader size={headerSize} text={headerMarkdown} />}
          fontSize={size.small}
          textContent={headerMarkdown}
          variation="title"
        />
      ) : null}
      {description ? <ABody className="p-0" bodyText={description} size={bodySize} color={bodyColor} /> : null}
    </LayeredFeatureTextBlockWrapper>
  );
}

function primarySecondarySizeClass(callToActionSize?: string): string {
  if (!callToActionSize) {
    return 'm-button-s';
  }
  const lowerCaseSize = callToActionSize.toLowerCase();
  switch (lowerCaseSize) {
    case size.small:
    case 'small':
      return 'm-button-s';
    case size.large:
    case 'large':
      return 'm-button-l';
    case size.medium:
    case 'medium':
      return 'm-button-m';
    default:
      return 'm-button-m';
  }
}

export function MerchandisingLayeredFeatureComponent(props: MerchandisingLayeredFeatureProps) {
  const {
    componentId,
    logo,
    eyebrowText,
    headerMedium = '',
    descriptionLong = '',
    callToActionText1,
    destinationUrl1,
    openInNewTab1,
    callToActionSize1,
    callToActionSize2,
    callToActionText2,
    destinationUrl2,
    openInNewTab2,
    tertiaryLinkText,
    tertiaryLinkUrl,
    tertiaryOpenInANewTab,
    backgroundImage,
    className = '',
    theme = 'inverse',
    caption,
    captionUrl,
    mboxParameter,
    isTargetContentLoaded = mboxParameter && !AuthoringUtils.isInEditor() ? false : true,
    type = 'cf',
    metadata,
    isTargetContentError = false,
    displayEvergreenContent,
    isHide = false,
  } = props;
  const hasPrimaryButton = Boolean(callToActionText1 && destinationUrl1);
  const hasSecondaryButton = Boolean(callToActionText2 && destinationUrl2);
  const hasTertiaryLink = Boolean(tertiaryLinkText && tertiaryLinkUrl);
  const math = useMathVariation(props);
  const isCoBrand = metadata?.type?.toLowerCase() === 'cobrand';
  const showMathDetails = isCoBrand && math?.variationDetails?.isVariationsAvailable;
  const themeClass = getStyleClassPerTheme(theme);
  const isInverse = themeClass === 'inverse';
  const isBrandIcon = /^(?:mcom-[^:]+:(?:brands)\/|marriott-icons:.+)/i;
  const brand = type === 'cf' ? logo?.imageSrc?.split('/').pop() : '';
  const { imageSrc, alternateDescription, renditions } = backgroundImage ?? {};
  const iconClass = getIconClassFromToken(logo?.imageSrc ?? '');
  const isDesktop = useCheckBreakpoint('viewportM');
  const primaryCtaRef = useRef<HTMLAnchorElement | null>(null);
  const secondaryCtaRef = useRef<HTMLAnchorElement | null>(null);
  const tertiaryCtaRef = useRef<HTMLAnchorElement | null>(null);
  const layeredFeatureCtaTracking = useLayeredFeatureButtonTracking(props);
  const logoImage = logo?.imageSrc ? (
    <img
      src={logo.imageSrc}
      alt={logo.alternateDescription ?? ''}
      className={clsx(
        isCoBrand && showMathDetails ? 'credit-card-image-math' : isCoBrand ? 'credit-card-image' : 'logo-image'
      )}
      data-testid="logo-image"
    />
  ) : null;

  if ((isTargetContentError && String(displayEvergreenContent) === 'false') || String(isHide) === 'true') {
    // here we are returning null to avoid rendering the component and performance issues
    // becoz we noticed that its triggering interact calls for tracking events even when the component is hidden, thats why we added this check
    return null;
  }

  const layeredFeature = (
    <StyledMerchandisingLayeredFeature
      className={clsx(className, !isTargetContentLoaded && 'skeleton-loader-composite')}
      $theme={theme}
      $type={type}
      data-testid={componentId || 'merch-MerchandisingLayeredFeature'}
      data-component-name={componentId || 'o-merch-MerchandisingLayeredFeature'}
    >
      {imageSrc || renditions?.length ? (
        <Image
          defaultImageURL={imageSrc}
          customClass="lyrd_ft__feature-img-wrapper"
          renditions={renditions}
          altText={alternateDescription}
        />
      ) : null}
      {caption ? (
        <StyledPhotoCaption
          as={captionUrl ? 'a' : 'div'}
          href={captionUrl || undefined}
          target="_self"
          className="p-1 pr-2"
          aria-label={caption}
        >
          <AIcon icon="icon-location d-flex align-items-center justify-content-center " size={AIconSize.S} />
          <StyledPhotoCaptionText className="t-font-xs">{caption}</StyledPhotoCaptionText>
        </StyledPhotoCaption>
      ) : null}
      <div
        className={clsx('lyrd_ft__cnt-sec lyrd_ft__gradient row d-flex', {
          lyrd_ft__dir: headerMedium || descriptionLong,
          't-color-inverse': isInverse,
        })}
      >
        <div
          className={clsx(
            'lyrd_ft__cnt-wrapper',
            'col-12 col-xl-5 offset-lg-1',
            showMathDetails ? 'col-md-8 math-details-padding-sm' : 'pb-4 col-md-6',
            'px-4 pr-md-2 pl-xl-2 pb-md-0'
          )}
        >
          {type === 'cf' && logo?.imageSrc?.match(isBrandIcon) ? (
            <MLogoContainer
              ariaRole="note"
              brand={brand || ''}
              aspectRatio="3:2"
              alignment="Center Left"
              width={135}
              height={90}
              color="background-aware"
              backgroundColor={
                isInverse ? MLogoContainerBackgroundColor.Inverse : MLogoContainerBackgroundColor.Standard
              }
              className="logo-icon"
            />
          ) : type === 'cf' && iconClass && logo?.imageSrc?.includes(':') ? (
            <Icon iconClass={clsx(iconClass, 'icon icon-xs logo-icon')} data-testid="logo-icon" />
          ) : (
            logoImage
          )}
          <div className={clsx('lyrd_ft__txt-cnt', { 'cb-banner__math': showMathDetails })}>
            <LayeredFeatureTextContent
              alignment={MTextBlockSimpleAlignment.Left}
              headerMarkdown={headerMedium}
              headerSize={AHeaderSize.TitleS}
              description={descriptionLong}
              bodySize={ABodySize.XS}
              bodyColor={ABodyColor.Default}
              eyebrowText={isCoBrand ? '' : eyebrowText}
            />
          </div>

          {showMathDetails && (
            <MathEquation className="col-11 col-md-12 px-0" isInverse={isInverse}>
              <EqColumn label={math.mathLabel1} value={math.value1} isInverse={isInverse} />
              <EqOperator operator="+" isInverse={isInverse} />
              <EqColumn label={math.mathLabel2} value={math.value2} isInverse={isInverse} />
              {isDesktop && <EqOperator operator="=" isInverse={isInverse} />}
              <EqColumn label={math.mathLabel3} value={math.value3} isTotal isInverse={isInverse} />
            </MathEquation>
          )}
          <div className="lyrd_ft__cta-cnt d-flex align-items-center">
            {isTargetContentLoaded && hasPrimaryButton && (
              <Button
                ref={primaryCtaRef}
                className={clsx(
                  'm-button-primary',
                  primarySecondarySizeClass(callToActionSize1),
                  openInNewTab1 === true && 'm-button-external'
                )}
                href={destinationUrl1}
                isLink={true}
                target={openInNewTab1 ? '_blank' : '_self'}
                rel={openInNewTab1 ? 'noopener noreferrer' : ''}
                linkType={layeredFeatureCtaTracking.primary.linkType}
                trackingProperties={layeredFeatureCtaTracking.primary.trackingProperties}
                buttonCopy={callToActionText1}
                ariaRole="button"
              />
            )}
            {isTargetContentLoaded && hasSecondaryButton && (
              <Button
                ref={secondaryCtaRef}
                className={clsx(
                  'm-button-secondary',
                  primarySecondarySizeClass(callToActionSize2),
                  openInNewTab2 === true && 'm-button-external'
                )}
                href={destinationUrl2}
                isLink={true}
                target={openInNewTab2 ? '_blank' : '_self'}
                rel={openInNewTab2 ? 'noopener noreferrer' : ''}
                linkType={layeredFeatureCtaTracking.secondary.linkType}
                trackingProperties={layeredFeatureCtaTracking.secondary.trackingProperties}
                buttonCopy={callToActionText2}
                ariaRole="button"
              />
            )}
            {isTargetContentLoaded && hasTertiaryLink && (
              <Button
                ref={tertiaryCtaRef}
                className={clsx(
                  'tertiary-merch-button',
                  tertiaryOpenInANewTab ? 'm-link-tertiary-button-external' : 'm-link-tertiary-button'
                )}
                href={tertiaryLinkUrl}
                isLink={true}
                target={tertiaryOpenInANewTab ? '_blank' : '_self'}
                rel={tertiaryOpenInANewTab ? 'noopener noreferrer' : ''}
                linkType={layeredFeatureCtaTracking.tertiary.linkType}
                trackingProperties={layeredFeatureCtaTracking.tertiary.trackingProperties}
                buttonCopy={tertiaryLinkText}
              />
            )}
          </div>
        </div>
      </div>
    </StyledMerchandisingLayeredFeature>
  );

  return !isTargetContentLoaded ? <SkeletonLoaderStyles>{layeredFeature}</SkeletonLoaderStyles> : layeredFeature;
}
